// ============================================================
// THE COMPANY — RoleProfile Component
// ============================================================

import { useState, useRef, useCallback, useEffect } from "react";
import DOMPurify from "dompurify";
import { useCompanyStore } from "../../store/useCompanyStore";
import RichTextEditor from "../shared/RichTextEditor";
import type { Role } from "../../lib/types";

interface RoleProfileProps {
  role: Role;
}

export default function RoleProfile({ role }: RoleProfileProps) {
  const updateRole = useCompanyStore((s) => s.updateRole);

  const [name, setName] = useState(role.name);
  const [title, setTitle] = useState(role.title);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [responsibilitiesOpen, setResponsibilitiesOpen] = useState(false);

  const personaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const responsibilitiesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (personaTimerRef.current) clearTimeout(personaTimerRef.current);
      if (responsibilitiesTimerRef.current)
        clearTimeout(responsibilitiesTimerRef.current);
    };
  }, []);

  // Sync local state when the selected role changes
  // (useEffect not needed since parent unmounts/remounts on role change via key)

  const handleNameBlur = () => {
    if (name !== role.name && name.trim()) {
      updateRole(role.id, { name: name.trim() });
    }
  };

  const handleTitleBlur = () => {
    if (title !== role.title && title.trim()) {
      updateRole(role.id, { title: title.trim() });
    }
  };

  const handlePersonaChange = useCallback(
    (html: string) => {
      if (personaTimerRef.current) clearTimeout(personaTimerRef.current);
      personaTimerRef.current = setTimeout(() => {
        updateRole(role.id, { persona: html });
      }, 500);
    },
    [role.id, updateRole],
  );

  const handleResponsibilitiesChange = useCallback(
    (html: string) => {
      if (responsibilitiesTimerRef.current)
        clearTimeout(responsibilitiesTimerRef.current);
      responsibilitiesTimerRef.current = setTimeout(() => {
        updateRole(role.id, { responsibilities: html });
      }, 500);
    },
    [role.id, updateRole],
  );

  return (
    <section className="space-y-4">
      {/* Name field */}
      <div>
        <label
          className="block text-xs font-medium mb-1.5"
          style={{ color: "var(--text-muted)" }}
        >
          Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          className="w-full rounded-lg px-3 py-2 text-sm font-medium outline-none transition-colors duration-150"
          style={{
            backgroundColor: "var(--bg-elevated)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-default)",
          }}
          placeholder="Role name"
        />
      </div>

      {/* Title field */}
      <div>
        <label
          className="block text-xs font-medium mb-1.5"
          style={{ color: "var(--text-muted)" }}
        >
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handleTitleBlur}
          className="w-full rounded-lg px-3 py-2 text-sm font-medium outline-none transition-colors duration-150"
          style={{
            backgroundColor: "var(--bg-elevated)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-default)",
          }}
          placeholder="Role title"
        />
      </div>

      {/* Persona — collapsible */}
      <CollapsibleSection
        label="Persona"
        isOpen={personaOpen}
        onToggle={() => setPersonaOpen((o) => !o)}
      >
        <RichTextEditor
          content={DOMPurify.sanitize(role.persona)}
          onChange={handlePersonaChange}
          placeholder="Describe the persona, personality, and backstory..."
        />
      </CollapsibleSection>

      {/* Responsibilities — collapsible */}
      <CollapsibleSection
        label="Responsibilities"
        isOpen={responsibilitiesOpen}
        onToggle={() => setResponsibilitiesOpen((o) => !o)}
      >
        <RichTextEditor
          content={DOMPurify.sanitize(role.responsibilities)}
          onChange={handleResponsibilitiesChange}
          placeholder="List key responsibilities for this role..."
        />
      </CollapsibleSection>
    </section>
  );
}

// --- Collapsible Section Helper ---

interface CollapsibleSectionProps {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CollapsibleSection({
  label,
  isOpen,
  onToggle,
  children,
}: CollapsibleSectionProps) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 w-full text-left text-xs font-medium mb-1.5 cursor-pointer"
        style={{ color: "var(--text-muted)" }}
      >
        <svg
          className="transition-transform duration-150"
          style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
        >
          <path
            d="M3 1.5L7 5L3 8.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {label}
      </button>
      {isOpen && <div className="fade-in">{children}</div>}
    </div>
  );
}
